import type { Store } from './store';
import type { AppState } from './appState';

export class PlaybackController {
  private raf: number | null = null;
  private lastTime = 0;
  private accumulator = 0;
  private readonly requestFrame: (cb: (t: number) => void) => number;
  private readonly cancelFrame: (id: number) => void;

  constructor(
    private readonly store: Store<AppState>,
    frame?: { request: (cb: (t: number) => void) => number; cancel: (id: number) => void },
  ) {
    this.requestFrame = frame?.request ?? ((cb) => requestAnimationFrame(cb));
    this.cancelFrame = frame?.cancel ?? ((id) => cancelAnimationFrame(id));
  }

  play(): void {
    const { playback, solution } = this.store.get();
    if (playback.playing) return;
    const index = playback.index >= solution.count ? 0 : playback.index;
    this.store.set({ playback: { ...playback, playing: true, index, follow: false } });
    this.lastTime = 0;
    this.accumulator = 0;
    this.schedule();
  }

  pause(): void {
    const { playback } = this.store.get();
    if (this.raf !== null) {
      this.cancelFrame(this.raf);
      this.raf = null;
    }
    if (playback.playing) this.store.set({ playback: { ...playback, playing: false } });
  }

  toggle(): void {
    if (this.store.get().playback.playing) this.pause();
    else this.play();
  }

  step(delta: number): void {
    this.pause();
    const { playback, solution } = this.store.get();
    const index = clamp(playback.index + delta, 0, solution.count);
    this.store.set({ playback: { ...playback, index, follow: false } });
  }

  restart(): void {
    this.pause();
    const { playback } = this.store.get();
    this.store.set({ playback: { ...playback, index: 0, follow: false } });
  }

  seek(index: number): void {
    const { playback, solution } = this.store.get();
    const clamped = clamp(Math.round(index), 0, solution.count);
    this.store.set({ playback: { ...playback, index: clamped, follow: clamped >= solution.count } });
  }

  toEnd(): void {
    this.pause();
    const { playback, solution } = this.store.get();
    this.store.set({ playback: { ...playback, index: solution.count, follow: true } });
  }

  setSpeed(stringsPerSecond: number): void {
    const { playback } = this.store.get();
    this.store.set({ playback: { ...playback, speed: clamp(stringsPerSecond, 1, 100_000) } });
  }

  tick(time: number): void {
    const { playback, solution } = this.store.get();
    if (!playback.playing) return;
    if (this.lastTime === 0) this.lastTime = time;
    const dt = Math.min(0.25, (time - this.lastTime) / 1000);
    this.lastTime = time;
    this.accumulator += dt * playback.speed;
    const advance = Math.floor(this.accumulator);
    if (advance > 0) {
      this.accumulator -= advance;
      const index = Math.min(solution.count, playback.index + advance);
      const finished = index >= solution.count;
      this.store.set({ playback: { ...playback, index, playing: !finished } });
      if (finished) {
        this.raf = null;
        return;
      }
    }
    this.schedule();
  }

  dispose(): void {
    this.pause();
  }

  private schedule(): void {
    if (this.raf !== null) return;
    this.raf = this.requestFrame((t) => {
      this.raf = null;
      this.tick(t);
    });
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
